
"use client";

import { useLanguage } from "@/app/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import {
  MapPin,
  Phone,
  MessageCircle,
  Sunrise,
  Flame,
  Sparkles,
  Send,
  User,
  Landmark,
  Clock,
  Star,
  Navigation,
} from "lucide-react";

const content = {
  en: {
    badge: "Reach Us",
    title: "Contact & Location",
    subtitle: "We welcome all devotees with open hearts",
    map: {
      title: "Temple Location",
      desc: "Kadavoor Thaluk, Kaniyalampatti Road, Sukkampatti, Karur",
    },
    address: {
      title: "Temple Address",
      line1: "18m Padi Karuppanasamy Temple",
      line2: "Kaniyalampatti Road, Sukkampatti",
      line3: "Kadavoor Thaluk, Karur District",
      line4: "Tamil Nadu, India",
    },
    phones: {
      title: "Phone Numbers",
      numbers: ["9597255401", "9361986095"],
    },
    timing: {
      title: "Temple Timings",
      allDays: "Open All 7 Days",
      rows: [
        { label: "Morning Darshan", time: "4:00 AM – 12:00 PM" },
        { label: "Evening Darshan", time: "4:00 PM – 7:00 PM" },
        { label: "Tuesday & Friday", time: "Full Day Open" },
        { label: "Ammavaasai & Pavarnamni", time: "Full Day Open" },
      ],
      poojas: [
        { name: "Brahma Muhurtha Pooja (Daily)", time: "4:30 AM – 6:00 AM" },
        { name: "Astami & Navami Special Pooja", time: "4:30 PM – 6:00 PM" },
      ],
    },
    whatsapp: {
      label: "Chat on WhatsApp",
      number: "919597255401",
    },
    form: {
      title: "Send a Message",
      name: "Your Name",
      namePlaceholder: "Enter your name",
      phone: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      message: "Message",
      messagePlaceholder: "Write your message or query here...",
      submit: "Send Message",
      success: "Message sent! We'll get back to you soon.",
      error: "Could not send your message. Please try again or call the temple.",
      sending: "Sending...",
    },
    trustee: {
      title: "Temple Management",
      trustee: "Trustee",
      name: "Kalimuthu",
      father: "Father: Vengatajalam",
      mother: "Mother: Palaniyamaal",
      brother: "Brother: Krishnamoorthy",
      staff: "2 dedicated male staff serve the temple",
    },
  },
  ta: {
    badge: "தொடர்பு கொள்ளுங்கள்",
    title: "தொடர்பு & இருப்பிடம்",
    subtitle: "அனைத்து பக்தர்களையும் அன்புடன் வரவேற்கிறோம்",
    map: {
      title: "கோவில் இருப்பிடம்",
      desc: "கடவூர் தாலுகா, கணியாலம்பட்டி ரோடு, சுக்கம்பட்டி, கரூர்",
    },
    address: {
      title: "கோவில் முகவரி",
      line1: "18ம் படி கருப்பண்ணசாமி கோவில்",
      line2: "கணியாலம்பட்டி ரோடு, சுக்கம்பட்டி",
      line3: "கடவூர் தாலுகா, கரூர் மாவட்டம்",
      line4: "தமிழ்நாடு, இந்தியா",
    },
    phones: {
      title: "தொலைபேசி எண்கள்",
      numbers: ["9597255401", "9361986095"],
    },
    timing: {
      title: "கோவில் நேரங்கள்",
      allDays: "வாரம் 7 நாட்களும் திறந்திருக்கும்",
      rows: [
        { label: "காலை தரிசனம்", time: "4:00 AM – 12:00 PM" },
        { label: "மாலை தரிசனம்", time: "4:00 PM – 7:00 PM" },
        { label: "செவ்வாய் & வெள்ளி", time: "முழு நேர தரிசனம்" },
        { label: "அமாவாசை & பவுர்ணமி", time: "முழு நேர தரிசனம்" },
      ],
      poojas: [
        { name: "பிரம்ம முகூர்த்த பூஜை (தினசரி)", time: "4:30 AM – 6:00 AM" },
        { name: "அஷ்டமி & நவமி சிறப்பு பூஜை", time: "4:30 PM – 6:00 PM" },
      ],
    },
    whatsapp: {
      label: "வாட்ஸ்அப்பில் தொடர்பு கொள்ளுங்கள்",
      number: "919597255401",
    },
    form: {
      title: "செய்தி அனுப்புங்கள்",
      name: "உங்கள் பெயர்",
      namePlaceholder: "உங்கள் பெயரை உள்ளிடுங்கள்",
      phone: "தொலைபேசி எண்",
      phonePlaceholder: "தொலைபேசி எண்ணை உள்ளிடுங்கள்",
      message: "செய்தி",
      messagePlaceholder: "உங்கள் செய்தி அல்லது கேள்வியை இங்கே எழுதுங்கள்...",
      submit: "செய்தி அனுப்பு",
      success: "செய்தி அனுப்பப்பட்டது! விரைவில் தொடர்பு கொள்கிறோம்.",
      error: "செய்தி அனுப்ப முடியவில்லை. மீண்டும் முயற்சிக்கவும் அல்லது கோவிலை அழைக்கவும்.",
      sending: "அனுப்புகிறது...",
    },
    trustee: {
      title: "கோவில் நிர்வாகம்",
      trustee: "நிர்வாகி",
      name: "களிமுத்து",
      father: "தந்தை: வெங்கடாசலம்",
      mother: "தாய்: பழனியம்மாள்",
      brother: "சகோதரர்: கிருஷ்ணமூர்த்தி",
      staff: "2 அன்பர்கள் கோவிலில் சேவை செய்கின்றனர்",
    },
  },
};

export default function ContactPage() {
  const { language } = useLanguage();
  const t = content[language as "en" | "ta"] ?? content.en;
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = (await response.json()) as { error?: string };

      if (!response.ok) {
        throw new Error(data.error ?? "Failed to send message");
      }

      setSubmitted(true);
      setForm({ name: "", phone: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch {
      setSubmitError(t.form.error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const whatsappUrl = `https://wa.me/${t.whatsapp.number}?text=${encodeURIComponent(
    language === "ta"
      ? "வணக்கம்! 18ம் படி கருப்பண்ணசாமி கோவிலில் விசாரிக்க விரும்புகிறேன்."
      : "Namaskaram! I would like to enquire about 18m Padi Karuppanasamy Temple."
  )}`;

  const deities =
    language === "ta"
      ? ["கருப்பண்ணசாமி", "காளி அம்மன்", "பைரவர்", "முருகன்", "விநாயகர்", "நவகிரகம்"]
      : ["Karuppanasamy", "Kaali Amman", "Bairavar", "Murugan", "Vinayagar", "Navagram"];

  return (
    <main className="bg-stone-950 text-stone-100 min-h-screen font-serif selection:bg-amber-900 selection:text-amber-100 overflow-x-hidden">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex flex-col items-center justify-center text-center px-4 py-32 overflow-hidden">

        {/* layered atmospheric bg */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/40 via-stone-950/80 to-stone-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,_rgba(180,83,9,0.18),_transparent)]" />

        {/* decorative concentric rings */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-amber-700/10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full border border-amber-600/10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] rounded-full border border-amber-500/10" />

        {/* top ornamental line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-700/60 to-transparent" />

        <div className="relative z-10 space-y-6 max-w-3xl mx-auto mt-10">
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-amber-600/60" />
            <Badge
              variant="outline"
              className="text-amber-400 border-amber-600/40 bg-amber-950/50 backdrop-blur px-5 py-1.5 text-[10px] tracking-[0.25em] uppercase font-sans font-semibold"
            >
              ✦ {t.badge} ✦
            </Badge>
            <div className="h-px w-12 bg-amber-600/60" />
          </div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold leading-[1.1] tracking-tight">
            <span className="bg-gradient-to-br from-amber-100 via-yellow-400 to-orange-500 bg-clip-text text-transparent">
              {t.title}
            </span>
          </h1>

          <p className="text-base sm:text-lg text-stone-400 font-sans font-light tracking-widest">
            -  {t.subtitle} - 
          </p>

          {/* decorative flame row */}
          <div className="flex items-center justify-center gap-4 pt-2">
            {[...Array(5)].map((_, i) => (
              <Flame
                key={i}
                className={`h-4 w-4 ${i === 2 ? "text-amber-400 h-6 w-6" : "text-amber-700/60"}`}
              />
            ))}
          </div>
        </div>

        {/* bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-stone-950 to-transparent" />
      </section>

      {/* ── MAP ──────────────────────────────────────────── */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(120,53,15,0.12),transparent_60%)]" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-12 max-w-6xl">

          {/* section header */}
          <div className="text-center mb-14 space-y-3">
            <p className="text-amber-600/80 font-sans text-xs tracking-[0.3em] uppercase font-semibold">
              ✦ Find Us ✦
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-400">
              {t.map.title}
            </h2>
            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-600" />
              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-600" />
            </div>
            <p className="text-stone-400 text-sm font-sans pt-1 flex items-center justify-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-amber-500" />
              {t.map.desc}
            </p>
          </div>

          {/* map wrapper with decorative border */}
          <div className="relative rounded-3xl overflow-hidden border border-amber-900/30 shadow-2xl shadow-amber-950/40">
            <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-amber-800/10 z-20 pointer-events-none" />
            {/* corner ornaments */}
            <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-amber-600/60 rounded-tl-lg z-20 pointer-events-none" />
            <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-amber-600/60 rounded-tr-lg z-20 pointer-events-none" />
            <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-amber-600/60 rounded-bl-lg z-20 pointer-events-none" />
            <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-amber-600/60 rounded-br-lg z-20 pointer-events-none" />

            <iframe
              title="Temple Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3383.410248085217!2d78.21896917427173!3d10.792580358897139!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3baa3f1041b0d51f%3A0x850ce1d3c4748c71!2sSri%2018m%20padi%20karupanasamy%20temple!5e1!3m2!1sen!2sin!4v1780481684628!5m2!1sen!2sin" 
              width="100%"
              height="460"
              className="w-full  contrast-125 brightness-75 block"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className="mt-6 flex justify-center">
            <a
              href="https://maps.app.goo.gl/MEc8MU5DP83cnyoj6"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="border-amber-700/50 text-amber-400 hover:bg-amber-950/40 hover:text-amber-300 rounded-full px-8 py-5 font-sans font-medium tracking-wider gap-2 group"
              >
                <Navigation className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                Open in Google Maps
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ── ADDRESS · PHONE · WHATSAPP ───────────────────── */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/8 to-transparent" />
        {/* horizontal rule with ornament */}
        <div className="absolute top-0 left-4 right-4 flex items-center gap-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-800/40 to-transparent" />
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-12 max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* Address */}
            <div className="group relative rounded-2xl bg-gradient-to-br from-stone-900 to-stone-950 border border-stone-800 hover:border-amber-800/60 transition-all duration-300 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-950/0 to-amber-900/0 group-hover:from-amber-950/20 group-hover:to-transparent transition-all duration-500" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-700/0 group-hover:via-amber-700/60 to-transparent transition-all duration-500" />
              <div className="relative p-8 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-amber-950/70 border border-amber-800/40 flex items-center justify-center text-amber-500 group-hover:scale-110 group-hover:border-amber-600/60 transition-all duration-300">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-amber-300 pt-2 leading-tight">
                    {t.address.title}
                  </h3>
                </div>
                <div className="h-px bg-gradient-to-r from-amber-900/60 to-transparent" />
                <div className="space-y-2 font-sans">
                  <p className="text-stone-100 font-semibold text-sm leading-snug">{t.address.line1}</p>
                  <p className="text-stone-400 text-sm">{t.address.line2}</p>
                  <p className="text-stone-400 text-sm">{t.address.line3}</p>
                  <p className="text-stone-500 text-xs mt-1 tracking-wide">{t.address.line4}</p>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="group relative rounded-2xl bg-gradient-to-br from-stone-900 to-stone-950 border border-stone-800 hover:border-amber-800/60 transition-all duration-300 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-950/0 to-transparent group-hover:from-amber-950/20 transition-all duration-500" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-700/0 group-hover:via-amber-700/60 to-transparent transition-all duration-500" />
              <div className="relative p-8 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-amber-950/70 border border-amber-800/40 flex items-center justify-center text-amber-500 group-hover:scale-110 group-hover:border-amber-600/60 transition-all duration-300">
                    <Phone className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-amber-300 pt-2 leading-tight">
                    {t.phones.title}
                  </h3>
                </div>
                <div className="h-px bg-gradient-to-r from-amber-900/60 to-transparent" />
                <div className="space-y-3">
                  {t.phones.numbers.map((num, i) => (
                    <a key={i} href={`tel:${num}`} className="flex items-center justify-between rounded-xl bg-stone-800/60 hover:bg-amber-950/50 border border-stone-700/50 hover:border-amber-800/50 px-4 py-3 transition-all duration-200 group/phone">
                      <span className="font-sans text-amber-400 text-sm font-semibold tracking-widest group-hover/phone:text-amber-300">+91 {num}</span>
                      <Phone className="h-3.5 w-3.5 text-stone-500 group-hover/phone:text-amber-500 transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="group relative rounded-2xl bg-gradient-to-br from-stone-900 to-stone-950 border border-stone-800 hover:border-green-800/60 transition-all duration-300 overflow-hidden sm:col-span-2 lg:col-span-1">
              <div className="absolute inset-0 bg-gradient-to-br from-green-950/0 to-transparent group-hover:from-green-950/20 transition-all duration-500" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-700/0 group-hover:via-green-700/40 to-transparent transition-all duration-500" />
              <div className="relative p-8 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-green-950/70 border border-green-800/40 flex items-center justify-center text-green-500 group-hover:scale-110 group-hover:border-green-600/60 transition-all duration-300">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-green-400 pt-2 leading-tight">WhatsApp</h3>
                </div>
                <div className="h-px bg-gradient-to-r from-green-900/60 to-transparent" />
                <p className="text-stone-400 text-sm font-sans leading-relaxed">
                  {language === "ta"
                    ? "எங்களுக்கு நேரடியாக வாட்ஸ்அப்பில் செய்தி அனுப்புங்கள்"
                    : "Send us a direct message on WhatsApp for quick responses"}
                </p>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-green-700 hover:bg-green-600 text-white rounded-xl font-sans font-semibold tracking-wide py-5 shadow-lg shadow-green-950/50 hover:shadow-green-900/60 transition-all duration-300 hover:scale-[1.02] gap-2">
                    <MessageCircle className="h-4 w-4" />
                    {t.whatsapp.label}
                  </Button>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── TIMINGS ──────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_50%,rgba(120,53,15,0.10),transparent)]" />
        {/* decorative horizontal rule */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-700/40 to-transparent" />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-12 max-w-6xl">
          <div className="text-center mb-16 space-y-3">
            <p className="text-amber-600/80 font-sans text-xs tracking-[0.3em] uppercase font-semibold">
              ✦ Schedule ✦
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-400">{t.timing.title}</h2>
            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-600" />
              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-600" />
            </div>
            <p className="text-amber-500/70 font-sans text-xs tracking-[0.2em] uppercase pt-1">
              {t.timing.allDays}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Darshan */}
            <div className="relative rounded-2xl overflow-hidden border border-stone-800 bg-gradient-to-br from-stone-900/90 to-stone-950">
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-700/50 to-transparent" />
              <div className="p-8 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-950/60 border border-amber-800/40 flex items-center justify-center">
                    <Sunrise className="h-5 w-5 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-200 font-sans">
                    {language === "ta" ? "தரிசன நேரங்கள்" : "Darshan Timings"}
                  </h3>
                </div>
                <div className="space-y-0">
                  {t.timing.rows.map((row, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-4 border-b border-stone-800/70 last:border-0 group/row hover:bg-amber-950/10 -mx-2 px-2 rounded-lg transition-colors"
                    >
                      <span className="text-stone-400 font-sans text-sm font-medium group-hover/row:text-stone-300 transition-colors">
                        {row.label}
                      </span>
                      <span className="text-amber-400 font-sans text-xs font-bold bg-amber-950/40 border border-amber-900/40 px-3 py-1.5 rounded-full tracking-wide">
                        {row.time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pooja */}
            <div className="relative rounded-2xl overflow-hidden border border-stone-800 bg-gradient-to-br from-stone-900/90 to-stone-950">
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-orange-700/50 to-transparent" />
              <div className="p-8 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-950/60 border border-orange-800/40 flex items-center justify-center">
                    <Flame className="h-5 w-5 text-orange-400" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-200 font-sans">
                    {language === "ta" ? "பூஜை நேரங்கள்" : "Pooja Timings"}
                  </h3>
                </div>
                <div className="space-y-5">
                  {t.timing.poojas.map((p, i) => (
                    <div key={i} className="rounded-xl bg-stone-800/40 border border-stone-700/40 p-5 space-y-3 hover:border-amber-800/40 hover:bg-amber-950/20 transition-all duration-200">
                      <p className="text-stone-200 font-sans text-sm font-semibold leading-snug">{p.name}</p>
                      <div className="flex items-center gap-2">
                        <Flame className="h-3 w-3 text-orange-400 shrink-0" />
                        <span className="text-orange-400 font-sans text-xs font-bold tracking-wider">{p.time}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* stats row */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="rounded-xl bg-stone-800/50 border border-stone-700/50 p-4 text-center">
                    <p className="text-amber-400 font-bold text-2xl">22ft</p>
                    <p className="text-stone-500 font-sans text-[10px] mt-1 uppercase tracking-widest">
                      {language === "ta" ? "சிலை உயரம்" : "Statue Height"}
                    </p>
                  </div>
                  <div className="rounded-xl bg-stone-800/50 border border-stone-700/50 p-4 text-center">
                    <p className="text-amber-400 font-bold text-2xl">2014</p>
                    <p className="text-stone-500 font-sans text-[10px] mt-1 uppercase tracking-widest">
                      {language === "ta" ? "திறப்பு ஆண்டு" : "Est. Year"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FORM + TRUSTEE ───────────────────────────────── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(120,53,15,0.14),transparent)]" />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-700/40 to-transparent" />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-12 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* ── Contact Form ── */}
            <div className="space-y-10">
              <div className="space-y-3">
                <p className="text-amber-600/80 font-sans text-xs tracking-[0.3em] uppercase font-semibold">
                  ✦ Get In Touch ✦
                </p>
                <h2 className="text-4xl md:text-5xl font-bold text-amber-400">{t.form.title}</h2>
                <div className="flex items-center gap-3">
                  <div className="h-px w-16 bg-gradient-to-r from-amber-600 to-transparent" />
                </div>
              </div>

              {submitted ? (
                <div className="rounded-2xl border border-green-800/40 bg-green-950/20 p-12 text-center space-y-5">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 rounded-full border border-amber-600/40 bg-amber-950/50 flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-amber-400" />
                    </div>
                  </div>
                  <p className="text-green-400 font-sans font-semibold text-lg">{t.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2.5">
                    <Label className="text-stone-300 font-sans text-sm font-semibold tracking-wide">
                      {t.form.name}
                    </Label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500 pointer-events-none" />
                      <Input
                        required
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder={t.form.namePlaceholder}
                        className="pl-11 bg-stone-900/80 border-stone-700 text-stone-100 placeholder:text-stone-600 focus:border-amber-600 focus-visible:ring-amber-600/20 rounded-xl h-13 font-sans"
                      />
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    <Label className="text-stone-300 font-sans text-sm font-semibold tracking-wide">
                      {t.form.phone}
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500 pointer-events-none" />
                      <Input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder={t.form.phonePlaceholder}
                        className="pl-11 bg-stone-900/80 border-stone-700 text-stone-100 placeholder:text-stone-600 focus:border-amber-600 focus-visible:ring-amber-600/20 rounded-xl h-13 font-sans"
                      />
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    <Label className="text-stone-300 font-sans text-sm font-semibold tracking-wide">
                      {t.form.message}
                    </Label>
                    <Textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                      placeholder={t.form.messagePlaceholder}
                      className="bg-stone-900/80 border-stone-700 text-stone-100 placeholder:text-stone-600 focus:border-amber-600 focus-visible:ring-amber-600/20 rounded-2xl resize-none font-sans"
                    />
                  </div>
                  {submitError ? (
                    <p className="text-red-400 font-sans text-sm">{submitError}</p>
                  ) : null}
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-700 via-amber-600 to-orange-600 hover:from-amber-600 hover:via-amber-500 hover:to-orange-500 text-white border-none shadow-xl shadow-amber-950/40 rounded-xl py-6 text-base font-sans font-semibold tracking-wider transition-all duration-300 hover:scale-[1.01] hover:shadow-amber-900/50 gap-2 disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {isSubmitting ? t.form.sending : t.form.submit}
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              )}
            </div>

            {/* ── Trustee + Deities ── */}
            <div className="space-y-6">

              {/* Trustee card */}
              <div className="relative rounded-2xl overflow-hidden border border-amber-900/30 bg-gradient-to-br from-amber-950/20 via-stone-900 to-stone-950">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-600/60 to-transparent" />
                {/* subtle glow */}
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-amber-600/8 rounded-full blur-3xl pointer-events-none" />

                <div className="relative p-8 space-y-6">
                  {/* trustee identity */}
                  <div className="flex items-center gap-5">
                    <div className="relative">
                      <div className="w-18 h-18 w-16 h-16 rounded-2xl bg-amber-950/70 border-2 border-amber-700/40 flex items-center justify-center text-amber-400 shadow-lg shadow-amber-950/40">
                        <User className="h-8 w-8" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-600 border-2 border-stone-950 flex items-center justify-center">
                        <Star className="h-2.5 w-2.5 text-stone-950 fill-stone-950" />
                      </div>
                    </div>
                    <div>
                      <p className="text-amber-600/80 font-sans text-[10px] tracking-[0.25em] uppercase font-semibold mb-1">
                        {t.trustee.trustee}
                      </p>
                      <h3 className="text-2xl font-bold text-stone-100">{t.trustee.name}</h3>
                    </div>
                  </div>

                  <div className="h-px bg-gradient-to-r from-amber-800/50 via-amber-700/30 to-transparent" />

                  {/* family */}
                  <div className="grid gap-2.5">
                    {[t.trustee.father, t.trustee.mother, t.trustee.brother].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 font-sans text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                        <span className="text-stone-400">{item}</span>
                      </div>
                    ))}
                  </div>

                  <div className="h-px bg-gradient-to-r from-amber-800/50 via-amber-700/30 to-transparent" />

                  {/* staff note */}
                  <div className="flex items-center gap-3 rounded-xl bg-stone-800/50 border border-stone-700/50 px-5 py-3">
                    <Landmark className="h-4 w-4 text-amber-500 shrink-0" />
                    <p className="text-stone-400 font-sans text-sm italic">{t.trustee.staff}</p>
                  </div>

                  {/* contact links */}
                  <div className="space-y-2.5">
                    {t.phones.numbers.map((num, i) => (
                      <a
                        key={i}
                        href={`tel:${num}`}
                        className="flex items-center justify-between rounded-xl bg-stone-800/50 hover:bg-amber-950/40 border border-stone-700/40 hover:border-amber-800/50 px-4 py-3.5 transition-all duration-200 group"
                      >
                        <span className="text-amber-400 font-sans text-sm font-semibold tracking-widest group-hover:text-amber-300">
                          +91 {num}
                        </span>
                        <Phone className="h-4 w-4 text-stone-500 group-hover:text-amber-500 transition-colors" />
                      </a>
                    ))}
                  </div>

                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-green-700 hover:bg-green-600 text-white rounded-xl font-sans font-semibold tracking-wide py-5 shadow-lg shadow-green-950/40 transition-all duration-300 hover:scale-[1.01] gap-2">
                      <MessageCircle className="h-4 w-4" />
                      {t.whatsapp.label}
                    </Button>
                  </a>
                </div>
              </div>

              {/* Deities */}
              <div className="relative rounded-2xl overflow-hidden border border-stone-800 bg-gradient-to-br from-stone-900/60 to-stone-950">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-800/40 to-transparent" />
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <p className="text-amber-500/80 font-sans text-[10px] tracking-[0.25em] uppercase font-semibold">
                      {language === "ta" ? "தெய்வங்கள்" : "Deities Worshipped"}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {deities.map((d, i) => (
                      <span
                        key={i}
                        className="px-3.5 py-1.5 rounded-full bg-stone-800/70 border border-stone-700/60 hover:border-amber-700/50 hover:bg-amber-950/30 text-amber-300 text-xs font-sans font-medium tracking-wide transition-all duration-200 cursor-default"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ORNAMENT ──────────────────────────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-amber-700/40 to-transparent" />
      <div className="py-8 flex items-center justify-center gap-4">
        <div className="h-px w-24 bg-gradient-to-r from-transparent to-amber-800/40" />
        <div className="flex items-center gap-2">
          {[...Array(3)].map((_, i) => (
            <Flame key={i} className={`${i === 1 ? "h-5 w-5 text-amber-500" : "h-3 w-3 text-amber-800/60"}`} />
          ))}
        </div>
        <div className="h-px w-24 bg-gradient-to-l from-transparent to-amber-800/40" />
      </div>

    </main>
  );
}